import type { RemoteAudioTrack } from "livekit-client"

interface SpeakerMeterRefs {
  audioContextRef: { current: AudioContext | null }
  peaksRef: { current: Record<string, number> }
  frameRef: { current: Record<string, number> }
  setLevels: (
    updater: (current: Record<string, number>) => Record<string, number>,
  ) => void
}

/**
 * Fade length shared by every gain ramp in this module — muting, Stop/Close
 * tearing playback down, and a speaker fading in on attach. Long enough to
 * be inaudible as a ramp, short enough to feel instant.
 */
export const ANTI_CLICK_FADE_SECONDS = 0.05

/**
 * Wires playback and a level meter for one subscribed speaker, returning
 * the `GainNode` that actually controls audible output. This gain starts at
 * 0 and ramps to its initial value (see `rampGainToMute` below) rather than
 * jumping there directly — a speaker can attach mid-conversation, so the
 * first samples through this node are already mid-waveform, and starting at
 * full gain would pop exactly like an unramped mute toggle does. Muting
 * ramps this same node's gain on the AudioContext's own clock (see
 * `useSimulationRoom`'s `toggleMute`) rather than the attached `<audio>`
 * element's `.volume` — a step there only updates once per animation frame
 * and is at the mercy of main-thread jank, so a ramp driven that way can
 * still land as an audible click; an AudioParam automation is sample-accurate
 * and immune to both.
 *
 * The attached `<audio>` element stays permanently muted: Chrome delivers
 * no audio to WebAudio at all unless the track is also attached to a media
 * element, but the element itself is never the audible path here — this
 * GainNode, wired straight to the context's destination, is.
 *
 * Each speaker's own running peak (`peaksRef`), rather than a multiplier
 * shared across speakers, keeps the meters comparable regardless of how
 * differently loud the sources were actually captured (telephony-narrowband
 * agent audio vs. synthesized TTS persona audio, say) — a fixed multiplier
 * common to both would instead just show whichever one happens to be louder
 * as maxed out and the other as barely moving. The peak also decays each
 * tick rather than holding forever, so a meter that was loud a while ago and
 * has since gone quiet settles back down instead of reading as
 * barely-moving relative to a peak it will never see again.
 */
export function attachSpeakerMeter(
  track: RemoteAudioTrack,
  name: string,
  { audioContextRef, peaksRef, frameRef, setLevels }: SpeakerMeterRefs,
  initialMuted: boolean,
): GainNode {
  const context = (audioContextRef.current ??= new AudioContext())
  void context.resume()
  const stream = new MediaStream([track.mediaStreamTrack])
  const source = context.createMediaStreamSource(stream)

  const playbackGain = context.createGain()
  playbackGain.gain.value = 0
  source.connect(playbackGain)
  playbackGain.connect(context.destination)
  rampGainToMute(context, playbackGain, initialMuted, ANTI_CLICK_FADE_SECONDS)

  const analyser = context.createAnalyser()
  analyser.fftSize = 512
  source.connect(analyser)

  // Chrome delivers nothing to a graph branch that does not itself reach
  // the destination, so the analyser needs its own (silent) path there even
  // though playback above already reaches it through a separate branch.
  const silent = context.createGain()
  silent.gain.value = 0
  analyser.connect(silent)
  silent.connect(context.destination)

  const PEAK_DECAY_PER_TICK = 0.98
  const MIN_PEAK = 0.01

  const buffer = new Float32Array(analyser.fftSize)
  const tick = () => {
    analyser.getFloatTimeDomainData(buffer)
    let sum = 0
    for (const value of buffer) {
      sum += value * value
    }
    const rms = Math.sqrt(sum / buffer.length)

    const previousPeak = peaksRef.current[name] ?? MIN_PEAK
    const peak = Math.max(rms, previousPeak * PEAK_DECAY_PER_TICK, MIN_PEAK)
    peaksRef.current[name] = peak

    setLevels((current) => ({ ...current, [name]: Math.min(1, rms / peak) }))
    frameRef.current[name] = requestAnimationFrame(tick)
  }
  tick()

  return playbackGain
}

/**
 * Ramps a speaker's playback gain toward muted/unmuted on the AudioContext's
 * own clock, so the fade is sample-accurate and immune to main-thread jank.
 *
 * The live value is read *before* cancelling: `cancelScheduledValues`
 * removes an in-flight ramp event entirely, after which the param reports
 * the value it had at the ramp's start — not where the ramp had actually
 * reached — so a read-after-cancel would make a toggle that interrupts a
 * fade jump discontinuously before reversing, which is exactly the click
 * this ramp exists to avoid. (Read-before-cancel is the portable stand-in
 * for `cancelAndHoldAtTime`, which Firefox does not implement.)
 */
export function rampGainToMute(
  context: AudioContext,
  gain: GainNode,
  muted: boolean,
  fadeSeconds: number,
) {
  const now = context.currentTime
  const held = gain.gain.value
  gain.gain.cancelScheduledValues(now)
  gain.gain.setValueAtTime(held, now)
  gain.gain.linearRampToValueAtTime(muted ? 0 : 1, now + fadeSeconds)
}

/**
 * Ramps every gain to 0 ahead of a teardown, returning whether there was
 * anything to fade. Cutting a live graph without this pops exactly like an
 * unramped mute toggle.
 */
export function fadeGainsForTeardown(
  context: AudioContext | null,
  gains: GainNode[],
): boolean {
  if (context === null || gains.length === 0) {
    return false
  }
  for (const gain of gains) {
    rampGainToMute(context, gain, true, ANTI_CLICK_FADE_SECONDS)
  }
  return true
}
